import type { RoofGenerationStageInput, RoofGenerationStageResult } from "building-types/roofs/roofGenerationTypes";
import { extrudeRoofTopFaces } from "../roofExtrusions";
import { getFirstSlope } from "../roofSlopes";
import { getExtraHipFaces } from "../roofTruncation";

/**
 * Generates the roof top faces
 */
export function generateRoofTopFaces({ subRoofOptions, subRoof }: RoofGenerationStageInput): RoofGenerationStageResult {
  const { id: subRoofId, roofVolume, roofPerimeterRails } = subRoof;
  const { roofData, roofInputSettings, issues } = subRoofOptions;

  const {
    baseHeight,
    leftSideSlopes,
    rightSideSlopes,
    perimeters,
    extensions,
    shedRoofHalfFace,
    extrusion,
    exactEdges,
    rectangularOverhangs,
    blocks,
    startFaces,
    endFaces,
    roofTopFaces,
  } = roofVolume;

  const { hasStartExtension, hasEndExtension } = extensions;
  const { offsetWallPerimeter } = perimeters;
  const { baseLeftOverhang, baseRightOverhang } = rectangularOverhangs;
  const { face: startFrontFace } = startFaces;
  const { face: endFrontFace } = endFaces;

  const { roofEdgeOverrides } = roofData;

  const { slope: firstSlopeLeft } = getFirstSlope(leftSideSlopes);
  const { slope: firstSlopeRight } = getFirstSlope(rightSideSlopes);
  const wallExtensionHeightLeft = firstSlopeLeft * baseLeftOverhang;
  const wallExtensionHeightRight = firstSlopeRight * baseRightOverhang;

  // collect the extra hip faces
  const extraHipFaces = getExtraHipFaces({
    subRoofId,
    roofEdgeOverrides,
    height: baseHeight + Math.min(wallExtensionHeightLeft, wallExtensionHeightRight),
    perimeter: offsetWallPerimeter,
    blocks,
    perimeters,
    baseHeight,
    roofPerimeterRails,
    exactEdges,
    extrusion,
    hasStartExtension,
    hasEndExtension,
    startFrontFace,
    endFrontFace,
    shedRoofHalfFace,
    annotationSettings: { ...roofInputSettings.roofPlanSettings, ...roofInputSettings.roofElevationPlanSettings },
    issues,
  });

  // extrude resulting top faces
  const roofTopPlates = extrudeRoofTopFaces(roofTopFaces, roofPerimeterRails);

  return {
    subRoof: {
      roofTopFaces,
      roofTopPlates,
    },
    temporaries: {
      extraHipFaces,
    },
  };
}
